import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { errorMessage, huhokaApi, huhokaKeys, type HuhokaStatus } from './api';
import { readCallback } from './huhoka';

type Outcome = { kind: 'working' } | { kind: 'connected'; status: HuhokaStatus } | { kind: 'failed'; message: string };

/**
 * `/integrations/huhoka/callback` - HuHoKa CRM sends the admin's browser back
 * here after Approve or Cancel when we started the connection. The BFF
 * finishes the back-channel exchange; this page only reports the outcome.
 */
export function HuhokaCallbackPage() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
  // The code is single use: React StrictMode runs effects twice in dev, and a
  // second exchange would burn nothing but show a spurious error.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const { state, code, error } = readCallback(location.search);
    // Drop code and state from the address bar and history.
    window.history.replaceState({}, '', location.pathname);

    if (!state) {
      setOutcome({ kind: 'failed', message: 'This page was opened without a connection attempt.' });
      return;
    }

    huhokaApi
      .complete({ state, ...(code ? { code } : {}), ...(error ? { error } : {}) })
      .then((status) => {
        void queryClient.invalidateQueries({ queryKey: huhokaKeys.status().slice(0, 3) });
        if (status.status === 'ACTIVE') {
          setOutcome({ kind: 'connected', status });
        } else {
          setOutcome({ kind: 'failed', message: status.lastError ?? 'The connection failed.' });
        }
      })
      .catch((err) => {
        setOutcome({ kind: 'failed', message: errorMessage(err, 'The connection could not be completed.') });
      });
  }, [location.pathname, location.search, queryClient]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--co-canvas)] p-4">
      <div
        className="w-full max-w-[480px] rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] px-7 py-6"
        style={{ boxShadow: 'var(--co-shadow-card)' }}
      >
        <p className="co-mono text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
          HuHoKa CRM
        </p>
        {outcome.kind === 'working' && (
          <p className="mt-3 text-[13px] text-[var(--co-ink-2)]">Finishing the connection...</p>
        )}
        {outcome.kind === 'connected' && (
          <>
            <h1 className="mt-2 text-[22px] font-bold text-[var(--co-ink)]">Connected</h1>
            <p className="mt-2 text-[13px] text-[var(--co-ink-2)]">
              This workspace is now linked to {outcome.status.remoteTenantName ?? 'your HuHoKa CRM organisation'}.
            </p>
          </>
        )}
        {outcome.kind === 'failed' && (
          <>
            <h1 className="mt-2 text-[22px] font-bold text-[var(--co-ink)]">Not connected</h1>
            <p className="mt-2 text-[13px] text-[var(--co-burgundy)]" role="alert">
              {outcome.message}
            </p>
          </>
        )}
        {outcome.kind !== 'working' && (
          <Link
            to="/integrations/connectors"
            className="mt-5 inline-block text-[13px] text-[var(--co-brass-ink)] hover:underline"
          >
            Back to Integrations
          </Link>
        )}
      </div>
    </div>
  );
}

export default HuhokaCallbackPage;
